"use client";

import { useState, type JSX } from "react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

interface CopyButtonProps {
  text: string;
  label?: string;
  ariaLabel?: string;
  className?: string;
  onAfterCopy?: () => void;
}

export default function CopyButton({
  text,
  label,
  ariaLabel,
  className,
  onAfterCopy,
}: CopyButtonProps): JSX.Element {
  const [copied, setCopied] = useState(false);

  const handleCopy = async (): Promise<void> => {
    if (copied) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success(label ? `${label} copied` : "Copied to clipboard");
      onAfterCopy?.();
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Failed to copy");
    }
  };

  const isIconOnly = !label;
  const tooltipText = copied ? "Copied!" : (ariaLabel || (label ? `Copy ${label}` : "Copy to clipboard"));
  const ariaText = ariaLabel || (label && label !== "Copy" ? `Copy ${label}` : "Copy to clipboard");

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          onClick={handleCopy}
          aria-label={copied ? "Copied to clipboard" : ariaText}
          className={cn(
            "btn-secondary py-1.5 text-[10px] font-bold uppercase tracking-wider inline-flex items-center transition-all active:scale-95",
            isIconOnly ? "px-2.5" : "px-3 gap-2",
            copied
              ? "bg-green-50 text-green-600 border-green-200/50 hover:bg-green-50"
              : "hover:border-primary/40",
            className
          )}
          type="button"
        >
          <div className="relative w-3.5 h-3.5 shrink-0">
            <svg
              className={cn(
                "absolute inset-0 w-3.5 h-3.5 transition-all duration-300 transform",
                copied ? "opacity-0 scale-50 rotate-90" : "opacity-100 scale-100 rotate-0"
              )}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
              />
            </svg>
            <svg
              className={cn(
                "absolute inset-0 w-3.5 h-3.5 text-green-600 transition-all duration-300 transform",
                copied ? "opacity-100 scale-100 rotate-0" : "opacity-0 scale-50 -rotate-90"
              )}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={3}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          {label && (
            <span className={cn(
              "relative text-left transition-all duration-200",
              "min-w-[32px]"
            )}>
              {copied ? "Copied" : label}
            </span>
          )}
        </button>
      </TooltipTrigger>
      <TooltipContent side="top" className="text-[10px] px-2 py-1 font-bold uppercase tracking-wider">
        {tooltipText}
      </TooltipContent>
    </Tooltip>
  );
}
